import { HOME_SECTIONS, type HomeSectionId } from "../homeSectionModel.ts"
import { HOME_GRID_COLUMNS, type HomeLayoutItem } from "./homeLayout"

/**
 * GC Default's editable page: five sections on ONE 12-column grid.
 *
 * Each section heading is itself a full-width grid item. That is the whole
 * trick: a widget's section is simply the heading above it, so section order,
 * section membership, position, width and height are all plain `x/y/w/h` in
 * the `DashboardViewV4.layout` array the dashboard already drafts, saves and
 * cancels — no second persisted structure, and no schema change in
 * `ezbuild_back`, which stores that array verbatim.
 *
 * It is also what makes dragging a widget into another section a real drag:
 * there is one grid, so the pointer can carry a widget across a heading.
 *
 * Geometry is in rows of `HOME_SECTION_ROW_HEIGHT` px with a 16px gutter, so
 * one row step is 24px and an item `h` rows tall is `24h - 16` px.
 */

export const HOME_SECTION_ROW_HEIGHT = 8
export const HOME_SECTION_MARGIN: [number, number] = [16, 16]
/** A heading row: 32px, the title line and its breathing room. */
export const SECTION_HEADER_H = 2

const SECTION_PREFIX = "section:"

export const sectionItemId = (id: HomeSectionId): string =>
  `${SECTION_PREFIX}${id}`

export const isSectionItem = (i: string): boolean =>
  i.startsWith(SECTION_PREFIX)

export const sectionOfItem = (i: string): HomeSectionId | null => {
  if (!isSectionItem(i)) return null
  const id = i.slice(SECTION_PREFIX.length)
  return (HOME_SECTIONS as readonly string[]).includes(id)
    ? (id as HomeSectionId)
    : null
}

/** One placeable unit of the page and the size it opens at. */
export interface HomeUnitSpec {
  id: string
  section: HomeSectionId
  w: number
  h: number
  minW: number
  minH: number
  /**
   * The unit is as tall as its content, measured on the page, and only its
   * width is the user's to change. For bands that wrap their tiles onto a
   * second line as they narrow: a fixed height would clip them.
   */
  autoHeight?: boolean
}

/** Height in px of an item `h` rows tall. */
export const rowsToPx = (h: number): number =>
  h * HOME_SECTION_ROW_HEIGHT + (h - 1) * HOME_SECTION_MARGIN[1]

/** The fewest rows that hold `px` of content. */
export const pxToRows = (px: number): number =>
  Math.max(
    1,
    Math.ceil(
      (px + HOME_SECTION_MARGIN[1]) /
        (HOME_SECTION_ROW_HEIGHT + HOME_SECTION_MARGIN[1])
    )
  )

/**
 * Applies measured content heights (in rows) to the units that size to their
 * content, then lays the page out again so nothing below them overlaps.
 */
export const withMeasuredHeights = (
  layout: readonly HomeLayoutItem[],
  heights: Readonly<Record<string, number>>
): HomeLayoutItem[] => {
  let changed = false
  const next = layout.map((item) => {
    const h = heights[item.i]
    if (!h || h === item.h) return item
    changed = true
    return { ...item, h }
  })
  return changed ? normalizeSections(next) : [...layout]
}

const overlaps = (a: HomeLayoutItem, b: HomeLayoutItem): boolean =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h

const bottomOf = (items: readonly HomeLayoutItem[]): number =>
  items.reduce((acc, item) => Math.max(acc, item.y + item.h), 0)

const byReadingOrder = (a: HomeLayoutItem, b: HomeLayoutItem): number =>
  a.y - b.y || a.x - b.x

/**
 * Vertical compaction with collision resolution — the same rule
 * react-grid-layout applies while dragging, run here so every keyboard and
 * menu edit lands exactly where a drag would have, and so a removed widget's
 * gap closes without waiting for the grid to re-lay itself out.
 *
 * Items are taken in reading order; each rises until something stops it,
 * then drops below anything it still overlaps.
 */
export const compactLayout = (
  layout: readonly HomeLayoutItem[]
): HomeLayoutItem[] => {
  const placed: HomeLayoutItem[] = []
  for (const original of [...layout].sort(byReadingOrder)) {
    const item = { ...original }
    item.x = Math.max(0, Math.min(item.x, HOME_GRID_COLUMNS - item.w))
    item.y = Math.min(item.y, bottomOf(placed))
    while (
      item.y > 0 &&
      !placed.some((other) => overlaps({ ...item, y: item.y - 1 }, other))
    ) {
      item.y -= 1
    }
    let collider = placed.find((other) => overlaps(item, other))
    while (collider) {
      item.y = collider.y + collider.h
      collider = placed.find((other) => overlaps(item, other))
    }
    placed.push(item)
  }
  return placed
}

export interface SectionBlock {
  section: HomeSectionId
  header: HomeLayoutItem
  items: HomeLayoutItem[]
}

/**
 * Reads the page back as sections: every widget belongs to the nearest
 * heading above it. A widget dragged above the first heading joins the first
 * section rather than floating outside every section.
 */
export const sectionBlocks = (
  layout: readonly HomeLayoutItem[]
): SectionBlock[] => {
  const sorted = [...layout].sort(byReadingOrder)
  const blocks: SectionBlock[] = []
  const orphans: HomeLayoutItem[] = []
  for (const item of sorted) {
    const section = sectionOfItem(item.i)
    if (section) {
      blocks.push({ section, header: item, items: [] })
    } else if (blocks.length) {
      blocks[blocks.length - 1].items.push(item)
    } else {
      orphans.push(item)
    }
  }
  if (blocks.length && orphans.length) {
    blocks[0].items = [
      ...orphans.map((item) => ({ ...item, y: blocks[0].header.y })),
      ...blocks[0].items
    ]
  }
  return blocks
}

/**
 * Lays blocks out top to bottom in the order given, each widget keeping its
 * place relative to its own heading, then compacts the whole page.
 */
export const stackBlocks = (
  blocks: readonly SectionBlock[]
): HomeLayoutItem[] => {
  const out: HomeLayoutItem[] = []
  let top = 0
  for (const block of blocks) {
    out.push({
      ...block.header,
      x: 0,
      y: top,
      w: HOME_GRID_COLUMNS,
      h: SECTION_HEADER_H
    })
    const contentTop = top + SECTION_HEADER_H
    const relative = compactLayout(
      block.items.map((item) => ({
        ...item,
        y: Math.max(0, item.y - (block.header.y + SECTION_HEADER_H))
      }))
    )
    for (const item of relative) out.push({ ...item, y: item.y + contentTop })
    top = contentTop + bottomOf(relative)
  }
  return out
}

/** Re-reads sections from positions and lays them out cleanly again. */
export const normalizeSections = (
  layout: readonly HomeLayoutItem[]
): HomeLayoutItem[] => stackBlocks(sectionBlocks(layout))

/** The first gap a `w x h` item fits in among `items`, left to right then down. */
const freeSlotIn = (
  items: readonly HomeLayoutItem[],
  w: number,
  h: number
): { x: number; y: number } => {
  const bottom = bottomOf(items)
  for (let y = 0; y <= bottom; y += 1) {
    for (let x = 0; x + w <= HOME_GRID_COLUMNS; x += 1) {
      const candidate = { i: "?", x, y, w, h }
      if (!items.some((item) => overlaps(candidate, item))) return { x, y }
    }
  }
  return { x: 0, y: bottom }
}

/** Appends an item to the end of a section's own area. */
const appendToBlock = (
  block: SectionBlock,
  item: HomeLayoutItem
): SectionBlock => {
  const contentTop = block.header.y + SECTION_HEADER_H
  const relative = block.items.map((other) => ({
    ...other,
    y: other.y - contentTop
  }))
  const slot = freeSlotIn(relative, item.w, item.h)
  return {
    ...block,
    items: [...block.items, { ...item, x: slot.x, y: slot.y + contentTop }]
  }
}

/** The page GC Default opens with: every unit in its section, in order. */
export const defaultSectionLayout = (
  units: readonly HomeUnitSpec[],
  sectionOrder: readonly HomeSectionId[] = HOME_SECTIONS
): HomeLayoutItem[] => {
  let blocks: SectionBlock[] = sectionOrder.map((section, index) => ({
    section,
    header: {
      i: sectionItemId(section),
      x: 0,
      y: index * 1000,
      w: HOME_GRID_COLUMNS,
      h: SECTION_HEADER_H
    },
    items: []
  }))
  for (const unit of units) {
    blocks = blocks.map((block) =>
      block.section === unit.section
        ? appendToBlock(block, {
            i: unit.id,
            x: 0,
            y: 0,
            w: unit.w,
            h: unit.h
          })
        : block
    )
  }
  return stackBlocks(blocks)
}

/**
 * Brings a stored layout in line with what this user can see now.
 *
 * Widgets no longer shown (removed, or not permitted) drop out; widgets
 * shown with no stored place (a new default, a widget re-added) go to the
 * end of their own section; a missing heading is restored at the end of the
 * page. An empty store is the default page. Nothing here is written back —
 * it is what the page RENDERS, so an automatic pass can never overwrite the
 * user's saved arrangement.
 */
export const reconcileSectionLayout = (
  stored: readonly HomeLayoutItem[],
  units: readonly HomeUnitSpec[]
): HomeLayoutItem[] => {
  const usable = stored.filter((item) =>
    isSectionItem(item.i) ? sectionOfItem(item.i) !== null : true
  )
  if (!usable.some((item) => isSectionItem(item.i))) {
    return defaultSectionLayout(units)
  }
  const shown = new Map(units.map((unit) => [unit.id, unit]))
  const kept = usable
    .filter((item) => isSectionItem(item.i) || shown.has(item.i))
    .map((item) => {
      const unit = shown.get(item.i)
      if (!unit) return item
      return {
        ...item,
        w: Math.min(HOME_GRID_COLUMNS, Math.max(item.w, unit.minW)),
        h: Math.max(item.h, unit.minH)
      }
    })
  let blocks = sectionBlocks(kept)
  for (const section of HOME_SECTIONS) {
    if (blocks.some((block) => block.section === section)) continue
    blocks = [
      ...blocks,
      {
        section,
        header: {
          i: sectionItemId(section),
          x: 0,
          y: bottomOf(kept) + blocks.length,
          w: HOME_GRID_COLUMNS,
          h: SECTION_HEADER_H
        },
        items: []
      }
    ]
  }
  const placed = new Set(kept.map((item) => item.i))
  for (const unit of units) {
    if (placed.has(unit.id)) continue
    blocks = blocks.map((block) =>
      block.section === unit.section
        ? appendToBlock(block, {
            i: unit.id,
            x: 0,
            y: 0,
            w: unit.w,
            h: unit.h
          })
        : block
    )
  }
  return stackBlocks(blocks)
}

/** Section order as the page currently reads it. */
export const sectionOrderOf = (
  layout: readonly HomeLayoutItem[]
): HomeSectionId[] => sectionBlocks(layout).map((block) => block.section)

/** Moves a whole section, its widgets with it, to `toIndex`. */
export const moveSection = (
  layout: readonly HomeLayoutItem[],
  section: HomeSectionId,
  toIndex: number
): HomeLayoutItem[] => {
  const blocks = sectionBlocks(layout)
  const from = blocks.findIndex((block) => block.section === section)
  if (from === -1) return [...layout]
  const target = Math.max(0, Math.min(blocks.length - 1, toIndex))
  if (target === from) return stackBlocks(blocks)
  const next = [...blocks]
  const [moved] = next.splice(from, 1)
  next.splice(target, 0, moved)
  return stackBlocks(next)
}

/**
 * Where a heading dropped by the pointer belongs: before every other heading
 * whose top it landed above. Read against the layout as it was BEFORE the
 * drag, because the grid shuffles the other headings around a dragged one.
 */
export const sectionIndexForDrop = (
  before: readonly HomeLayoutItem[],
  section: HomeSectionId,
  dropY: number
): number =>
  sectionBlocks(before).filter(
    (block) => block.section !== section && block.header.y < dropY
  ).length

/** Moves a widget to the end of another section, keeping its size. */
export const moveToSection = (
  layout: readonly HomeLayoutItem[],
  id: string,
  section: HomeSectionId
): HomeLayoutItem[] => {
  const item = layout.find((candidate) => candidate.i === id)
  if (!item || isSectionItem(id)) return [...layout]
  const blocks = sectionBlocks(layout.filter((other) => other.i !== id)).map(
    (block) =>
      block.section === section ? appendToBlock(block, { ...item }) : block
  )
  return stackBlocks(blocks)
}

/** Drops a widget; the section closes up behind it. */
export const removeFromLayout = (
  layout: readonly HomeLayoutItem[],
  id: string
): HomeLayoutItem[] => normalizeSections(layout.filter((item) => item.i !== id))

/**
 * Holds `id` where it is and pushes everything it now covers down, in reading
 * order, each pushed item pushing what it lands on in turn — headings
 * included, so a pushed widget never slides under the next section's heading.
 */
const pushDownFrom = (
  layout: readonly HomeLayoutItem[],
  id: string
): HomeLayoutItem[] => {
  const self = layout.find((item) => item.i === id)
  if (!self) return [...layout]
  const placed: HomeLayoutItem[] = [self]
  for (const original of readingOrder(layout)) {
    if (original.i === id) continue
    const item = { ...original }
    let collider = placed.find((other) => overlaps(item, other))
    while (collider) {
      item.y = collider.y + collider.h
      collider = placed.find((other) => overlaps(item, other))
    }
    placed.push(item)
  }
  return placed
}

/** Sets a widget's width, floored at its minimum and kept on the canvas. */
export const setWidth = (
  layout: readonly HomeLayoutItem[],
  id: string,
  w: number,
  minW: number
): HomeLayoutItem[] => {
  const width = Math.min(HOME_GRID_COLUMNS, Math.max(w, minW))
  const widened = layout.map((item) =>
    item.i === id
      ? { ...item, w: width, x: Math.min(item.x, HOME_GRID_COLUMNS - width) }
      : item
  )
  // Whatever the widened widget now covers steps below it rather than
  // overlapping, then settles left on its new row.
  return settleDisplaced(layout, pushDownFrom(widened, id), id)
}

/** Grows or shrinks a widget by `delta` rows, never below its minimum. */
export const resizeHeight = (
  layout: readonly HomeLayoutItem[],
  id: string,
  delta: number,
  minH: number
): HomeLayoutItem[] =>
  settleDisplaced(
    layout,
    pushDownFrom(
      layout.map((item) =>
        item.i === id ? { ...item, h: Math.max(minH, item.h + delta) } : item
      ),
      id
    ),
    id
  )

export type ReadingDirection = "earlier" | "later"

/**
 * The keyboard's Move up / Move down. Swaps a widget with its neighbour in
 * reading order; at the edge of a section it crosses into the next one, so
 * every position on the page is reachable without a pointer.
 */
export const moveInReadingOrder = (
  layout: readonly HomeLayoutItem[],
  id: string,
  direction: ReadingDirection
): HomeLayoutItem[] => {
  const blocks = sectionBlocks(layout)
  const blockIndex = blocks.findIndex((block) =>
    block.items.some((item) => item.i === id)
  )
  if (blockIndex === -1) return [...layout]
  const block = blocks[blockIndex]
  const items = [...block.items].sort(byReadingOrder)
  const index = items.findIndex((item) => item.i === id)
  const neighbourIndex = direction === "earlier" ? index - 1 : index + 1

  if (neighbourIndex < 0 || neighbourIndex >= items.length) {
    const nextBlock = blocks[blockIndex + (direction === "earlier" ? -1 : 1)]
    if (!nextBlock) return [...layout]
    if (direction === "later") {
      // Into the next section, first in line.
      const moving = items[index]
      const shifted = nextBlock.items.map((item) => ({
        ...item,
        y: item.y + moving.h
      }))
      const rebuilt = blocks.map((candidate, at) => {
        if (at === blockIndex)
          return {
            ...candidate,
            items: candidate.items.filter((item) => item.i !== id)
          }
        if (at === blockIndex + 1)
          return {
            ...candidate,
            items: [
              {
                ...moving,
                x: 0,
                y: nextBlock.header.y + SECTION_HEADER_H
              },
              ...shifted
            ]
          }
        return candidate
      })
      return stackBlocks(rebuilt)
    }
    return moveToSection(layout, id, nextBlock.section)
  }

  const a = items[index]
  const b = items[neighbourIndex]
  const swapped = layout.map((item) => {
    if (item.i === a.i) return { ...item, x: b.x, y: b.y }
    if (item.i === b.i) return { ...item, x: a.x, y: a.y }
    return item
  })
  return normalizeSections(swapped)
}

/** One column step left or right, for a widget narrower than the canvas. */
export const nudgeHorizontally = (
  layout: readonly HomeLayoutItem[],
  id: string,
  direction: "left" | "right"
): HomeLayoutItem[] => {
  const item = layout.find((candidate) => candidate.i === id)
  if (!item) return [...layout]
  const x =
    direction === "left"
      ? Math.max(0, item.x - 1)
      : Math.min(HOME_GRID_COLUMNS - item.w, item.x + 1)
  if (x === item.x) return [...layout]
  // Anything the widget now overlaps steps down rather than being covered.
  const moved = layout.map((candidate) =>
    candidate.i === id ? { ...candidate, x } : candidate
  )
  const others = moved.filter((candidate) => candidate.i !== id)
  const self = moved.find((candidate) => candidate.i === id)!
  const pushed = others.map((other) =>
    !isSectionItem(other.i) && overlaps(self, other)
      ? { ...other, y: self.y + self.h }
      : other
  )
  return normalizeSections([self, ...pushed])
}

/** Widgets in the order a reader (and the stacked phone layout) meets them. */
export const readingOrder = (
  layout: readonly HomeLayoutItem[]
): HomeLayoutItem[] => [...layout].sort(byReadingOrder)

/**
 * What the page draws: the stored page minus headings with nothing under
 * them. Used outside Edit layout only — an empty section in edit mode keeps
 * its heading so there is somewhere to drop a widget into it.
 */
export const withoutEmptySections = (
  layout: readonly HomeLayoutItem[]
): HomeLayoutItem[] =>
  stackBlocks(sectionBlocks(layout).filter((block) => block.items.length > 0))

/**
 * A pointer drop onto a widget of the same size swaps the two.
 *
 * The grid's own rule pushes whatever the dropped widget lands on DOWN, so
 * dragging Financial Snapshot onto Cash Flow's place left Cash Flow under it
 * and an empty half row beside it. Two equal cards trading places is what
 * "put this one before that one" means. Returns `null` when the drop is not
 * such a swap, and the grid's own result stands.
 */
export const swapOnDrop = (
  before: readonly HomeLayoutItem[],
  id: string,
  x: number,
  y: number
): HomeLayoutItem[] | null => {
  const moving = before.find((item) => item.i === id)
  if (!moving || isSectionItem(id)) return null
  const target = { ...moving, x, y }
  const covered = before.filter(
    (item) => item.i !== id && !isSectionItem(item.i) && overlaps(target, item)
  )
  if (covered.length !== 1) return null
  const other = covered[0]
  const sameFootprint = other.w === moving.w && other.h === moving.h
  const mostlyOver =
    Math.abs(other.x - x) * 2 < other.w && Math.abs(other.y - y) * 2 < other.h
  if (!sameFootprint || !mostlyOver) return null
  return normalizeSections(
    before.map((item) => {
      if (item.i === id) return { ...item, x: other.x, y: other.y }
      if (item.i === other.i) return { ...item, x: moving.x, y: moving.y }
      return item
    })
  )
}

/**
 * A widget the user did not touch but that a move or resize pushed onto a new
 * row slides to the leftmost free place on that row.
 *
 * Vertical compaction only ever moves things UP, so widening Financial
 * Snapshot to full width pushed Cash Flow down still sitting in the right
 * half, with an empty half row beside it. Widgets that kept their row keep
 * their column: the user may have put them there.
 */
export const settleDisplaced = (
  before: readonly HomeLayoutItem[],
  after: readonly HomeLayoutItem[],
  actorId: string
): HomeLayoutItem[] => {
  const was = new Map(before.map((item) => [item.i, item]))
  let next = normalizeSections(after)
  for (const item of readingOrder(next)) {
    if (item.i === actorId || isSectionItem(item.i)) continue
    const old = was.get(item.i)
    if (!old || old.y === item.y || item.x === 0) continue
    const others = next.filter((other) => other.i !== item.i)
    for (let x = 0; x < item.x; x += 1) {
      const candidate = { ...item, x }
      if (!others.some((other) => overlaps(candidate, other))) {
        next = next.map((other) => (other.i === item.i ? candidate : other))
        break
      }
    }
  }
  return normalizeSections(next)
}
